import { Analytics } from '@cobliteam/analytics';
import Collapsible, { CollapsibleContent, CollapsibleTrigger } from '@cobliteam/colib-ui/Collapsible';
import { cn } from '@cobliteam/colib-ui/cn';
import Icon, { type IconName } from '@cobliteam/colib-ui/Icon';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@cobliteam/colib-ui/InputGroup';
import type { RouteDef } from '@cobliteam/dashboard/src/dashboardDef';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useLanguage, useTranslation } from '@cobliteam/i18n';
import { getTextForLanguage } from '@cobliteam/ui-business/utils';
import { type KeyboardEvent, useRef, useState } from 'react';
import LinkWrapper from './LinkWrapper';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu as SidebarMenuBase,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from './Sidebar';

const mobileMenuItemClassName =
  'h-11 rounded-lg px-2.5 font-medium text-sidebar-foreground text-sm leading-5 transition-colors duration-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:[&>svg]:text-sidebar-accent-foreground data-[active=true]:bg-sidebar-primary data-[active=true]:hover:bg-sidebar-primary data-[active=true]:hover:text-sidebar-primary-foreground data-[active=true]:font-semibold data-[active=true]:text-sidebar-primary-foreground data-[active=true]:[&>svg]:text-sidebar-primary-foreground data-[active=true]:hover:[&>svg]:text-sidebar-primary-foreground [&>svg]:text-sidebar-foreground motion-reduce:transition-none';
const mobileSectionTriggerClassName =
  'group/section-trigger mx-3 flex h-11 w-[calc(100%-1.5rem)] items-center justify-between gap-2 rounded-lg px-2.5 font-medium text-sidebar-foreground text-sidebar-section leading-5 outline-none transition-colors duration-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-0 motion-reduce:transition-none';
const collapsibleContentClassName = 'overflow-hidden data-closed:animate-collapsible-up data-open:animate-collapsible-down motion-reduce:animate-none';
const mobileLinkClassName = 'rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-0';
const normalizeSearch = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
const getRouteTitle = (route: RouteDef, language: string) => getTextForLanguage(route.sidebar?.title, language);
const routeMatchesSearch = (route: RouteDef, search: string, language: string) => normalizeSearch(getRouteTitle(route, language)).includes(search);
const routeHasActiveDescendant = (route: RouteDef): boolean => Boolean(route.sidebar?.active || route.routes?.some(routeHasActiveDescendant));
const getVisibleRoutes = (routes: RouteDef[] | undefined): RouteDef[] | undefined => {
  if (!routes) return undefined;
  return routes
    .filter((route) => !route.sidebar?.hide)
    .map((route) => ({
      ...route,
      routes: getVisibleRoutes(route.routes),
    }));
};
const filterRoutesBySearch = (routes: RouteDef[], search: string, language: string): RouteDef[] => {
  if (!search) return getVisibleRoutes(routes) ?? [];
  return routes.reduce<RouteDef[]>((filteredRoutes, route) => {
    if (route.sidebar?.hide) return filteredRoutes;
    const matchesRoute = routeMatchesSearch(route, search, language);
    const filteredChildren = route.routes ? filterRoutesBySearch(route.routes, search, language) : undefined;
    if (matchesRoute || (filteredChildren && filteredChildren.length > 0)) {
      filteredRoutes.push({
        ...route,
        routes: matchesRoute ? getVisibleRoutes(route.routes) : filteredChildren,
      });
    }
    return filteredRoutes;
  }, []);
};
type HandleToggle = (id: string, title: string, isOpen: boolean) => void;
interface MobileRouteItemProps {
  route: RouteDef;
  currentLanguage: string;
  normalizedSearch: string;
  openGroups: Record<string, boolean>;
  onGroupToggle: HandleToggle;
  onNavigate: (destination: string) => void;
}
const MobileRouteItem = ({ route, currentLanguage, normalizedSearch, openGroups, onGroupToggle, onNavigate }: MobileRouteItemProps) => {
  const routeTitle = getRouteTitle(route, currentLanguage);
  const visibleChildren = route.routes?.filter((item) => !item.sidebar?.hide);
  if (visibleChildren?.length) {
    return (
      <SidebarGroup>
        <Collapsible
          className='group/collapsible'
          open={normalizedSearch ? true : (openGroups[route.id] ?? routeHasActiveDescendant(route))}
          onOpenChange={(isOpen) => onGroupToggle(route.id, routeTitle || '', isOpen)}
        >
          <CollapsibleTrigger className='group/trigger mx-3 flex h-11 w-[calc(100%-1.5rem)] items-center justify-between gap-2 rounded-lg px-2.5 font-medium text-sidebar-foreground text-sm leading-5 outline-none transition-colors duration-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-0 motion-reduce:transition-none'>
            <span className='flex min-w-0 items-center gap-3'>
              {route.sidebar?.icon && (
                <Icon name={route.sidebar.icon as IconName} size='md' className='shrink-0 text-sidebar-foreground group-hover/trigger:text-sidebar-accent-foreground' />
              )}
              <span className='truncate'>{routeTitle}</span>
            </span>
            <Icon
              name='chevron-down'
              size='sm'
              className='ml-auto shrink-0 text-sidebar-foreground transition-transform duration-200 group-hover/trigger:text-sidebar-accent-foreground group-data-open/collapsible:rotate-180 motion-reduce:transition-none'
            />
          </CollapsibleTrigger>
          <CollapsibleContent className={collapsibleContentClassName}>
            <SidebarGroupContent>
              <SidebarMenuSub className='mx-5 my-0.5 gap-0 border-sidebar-border py-0.5 pr-0 pl-3 motion-reduce:transition-none'>
                {visibleChildren.map((item) => {
                  const itemTitle = getRouteTitle(item, currentLanguage);
                  return (
                    <SidebarMenuSubItem key={item.id}>
                      <SidebarMenuSubButton
                        isActive={item.sidebar?.active}
                        className='min-h-11 rounded-lg px-2.5 font-medium text-sidebar-foreground text-sm leading-5 transition-colors duration-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground data-[active=true]:bg-sidebar-primary data-[active=true]:font-semibold data-[active=true]:text-sidebar-primary-foreground data-[active=true]:hover:bg-sidebar-primary data-[active=true]:hover:text-sidebar-primary-foreground motion-reduce:transition-none'
                        render={
                          <LinkWrapper to={item.redirect || item.url || '#'} pageId={item.id} className={mobileLinkClassName} onClick={() => onNavigate(itemTitle)}>
                            {itemTitle}
                          </LinkWrapper>
                        }
                      />
                    </SidebarMenuSubItem>
                  );
                })}
              </SidebarMenuSub>
            </SidebarGroupContent>
          </CollapsibleContent>
        </Collapsible>
      </SidebarGroup>
    );
  }
  return (
    <SidebarGroup>
      <SidebarMenuBase className='px-3'>
        <SidebarMenuItem>
          <LinkWrapper to={route.redirect || route.url || '#'} pageId={route.id} className={mobileLinkClassName} onClick={() => onNavigate(routeTitle)}>
            <SidebarMenuButton tooltip={routeTitle} size='sm' className={mobileMenuItemClassName} isActive={route.sidebar?.active}>
              {route.sidebar?.icon && <Icon name={route.sidebar.icon as IconName} size='md' />}
              <span>{routeTitle}</span>
            </SidebarMenuButton>
          </LinkWrapper>
        </SidebarMenuItem>
      </SidebarMenuBase>
    </SidebarGroup>
  );
};
interface MobileSidebarSectionProps {
  route: RouteDef;
  index: number;
  currentLanguage: string;
  normalizedSearch: string;
  isOpen: boolean;
  openGroups: Record<string, boolean>;
  onSectionToggle: HandleToggle;
  onGroupToggle: HandleToggle;
  onNavigate: (destination: string) => void;
}
const MobileSidebarSection = ({ route, index, currentLanguage, normalizedSearch, isOpen, openGroups, onSectionToggle, onGroupToggle, onNavigate }: MobileSidebarSectionProps) => {
  const routeTitle = getRouteTitle(route, currentLanguage);
  const visibleChildren = route.routes?.filter((item) => !item.sidebar?.hide);
  if (!visibleChildren?.length) {
    return (
      <SidebarGroup className={cn(index === 0 && 'mt-1')}>
        <SidebarMenuBase className='px-3'>
          <SidebarMenuItem>
            <LinkWrapper to={route.redirect || route.url || '#'} pageId={route.id} className={mobileLinkClassName} onClick={() => onNavigate(routeTitle)}>
              <SidebarMenuButton tooltip={routeTitle} size='sm' className={mobileMenuItemClassName} isActive={route.sidebar?.active}>
                {route.sidebar?.icon && <Icon name={route.sidebar.icon as IconName} size='md' />}
                <span>{routeTitle}</span>
              </SidebarMenuButton>
            </LinkWrapper>
          </SidebarMenuItem>
        </SidebarMenuBase>
      </SidebarGroup>
    );
  }
  return (
    <SidebarGroup className={cn(index === 0 && 'mt-1')}>
      <Collapsible className='group/section' open={isOpen} onOpenChange={(nextIsOpen) => onSectionToggle(route.id, routeTitle || '', nextIsOpen)}>
        <CollapsibleTrigger data-testid='sidebar-section-title' className={mobileSectionTriggerClassName}>
          <span className='truncate'>{routeTitle}</span>
          <Icon
            name='chevron-down'
            size='sm'
            className='ml-auto shrink-0 text-sidebar-foreground transition-transform duration-200 group-hover/section-trigger:text-sidebar-accent-foreground group-data-open/section:rotate-180 motion-reduce:transition-none'
          />
        </CollapsibleTrigger>
        <CollapsibleContent className={collapsibleContentClassName}>
          <div className='pt-0.5'>
            {visibleChildren.map((item) => (
              <MobileRouteItem
                key={item.id}
                route={item}
                currentLanguage={currentLanguage}
                normalizedSearch={normalizedSearch}
                openGroups={openGroups}
                onGroupToggle={onGroupToggle}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </SidebarGroup>
  );
};
export function SidebarMenuMobile() {
  const { sidebar } = useDashboard();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation('ui-business');
  const { setOpenMobile } = useSidebar();
  const [openSectionId, setOpenSectionId] = useState<string | null>();
  const [openGroups, setOpenGroups] = useState<{
    [key: string]: boolean;
  }>({});
  const [search, setSearch] = useState('');
  const menuListRef = useRef<HTMLDivElement>(null);
  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Tab' || event.shiftKey) return;
    const firstFocusable = menuListRef.current?.querySelector<HTMLElement>('a[href], button:not([disabled])');
    if (!firstFocusable) return;
    event.preventDefault();
    firstFocusable.focus();
  };
  const router = sidebar.router;
  const normalizedSearch = normalizeSearch(search);
  const filteredRouter = filterRoutesBySearch(router, normalizedSearch, currentLanguage);
  const handleGroupToggle = (id: string, title: string, isOpen: boolean) => {
    Analytics.track('Sidebar - Group - Toggle', {
      title,
      isOpen,
    });
    setOpenGroups((prev) => ({
      ...prev,
      [id]: isOpen,
    }));
  };
  const handleSectionToggle = (id: string, title: string, isOpen: boolean) => {
    Analytics.track('Sidebar - Group - Toggle', {
      title,
      isOpen,
    });
    setOpenSectionId(isOpen ? id : null);
  };
  const handleNavigate = (destination: string) => {
    setOpenMobile(false);
    Analytics.track('Sidebar - Navigate', {
      destination,
    });
  };
  return (
    <div className='flex min-h-0 flex-col pb-6'>
      <div className='sticky top-0 z-10 border-sidebar-border border-b bg-sidebar px-3 py-2.5'>
        <InputGroup size='sm' className={'!border-sidebar-border focus-visible:!border-sidebar-ring bg-sidebar text-sidebar-foreground placeholder:text-sidebar-foreground'}>
          <InputGroupAddon align='inline-start'>{<Icon name='search' size='md' className='text-sidebar-foreground' />}</InputGroupAddon>
          <InputGroupInput
            data-testid='sidebar-mobile-search'
            name='sidebar-mobile-search'
            placeholder={t('app_sidebar.search_placeholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {search && (
            <InputGroupAddon align='inline-end'>
              {
                <button
                  type='button'
                  aria-label={t('app_sidebar.clear_search')}
                  className='flex size-8 items-center justify-center rounded-lg text-sidebar-foreground transition-colors duration-200 hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring motion-reduce:transition-none'
                  onClick={() => setSearch('')}
                >
                  <Icon name='x' size='sm' />
                </button>
              }
            </InputGroupAddon>
          )}
        </InputGroup>
      </div>
      <div ref={menuListRef}>
        {filteredRouter.length === 0 && (
          <div className='mx-3 mt-8 flex flex-col items-center gap-2 rounded-xl border border-sidebar-border bg-sidebar-accent px-4 py-6 text-center'>
            <Icon name='search' size='md' className='text-sidebar-foreground' />
            <p className='font-medium text-sidebar-foreground text-sm'>{t('app_sidebar.empty_search_title')}</p>
            <p className='text-sidebar-foreground text-sm'>{t('app_sidebar.empty_search_description')}</p>
          </div>
        )}
        {filteredRouter.map((route, index) => (
          <MobileSidebarSection
            key={route.id}
            route={route}
            index={index}
            currentLanguage={currentLanguage}
            normalizedSearch={normalizedSearch}
            isOpen={normalizedSearch ? true : openSectionId === undefined ? routeHasActiveDescendant(route) : openSectionId === route.id}
            openGroups={openGroups}
            onSectionToggle={handleSectionToggle}
            onGroupToggle={handleGroupToggle}
            onNavigate={handleNavigate}
          />
        ))}
      </div>
    </div>
  );
}
